import React, { useEffect, useRef, useState } from 'react';
import { XIcon, CopyIcon, CheckIcon, ExternalLinkIcon } from 'lucide-react';
import { Button } from '../ui/Button';
import { composeUrl, getSenderEmail, FIRM_EMAIL, type MailClient } from '../../utils/mailer';
export { FIRM_EMAIL };
interface Props {
  to: string;
  subject: string;
  body: string;
  onClose: () => void;
  /** Called once the message has been handed to a mail composer. */
  onSent: () => void;
}

/**
 * Alternative ways to open the same message, for when the preferred mail
 * client is not the one configured. Sending itself always happens in a real
 * mailbox, so the candidate receives an ordinary email.
 */
export function SendDialog({
  to,
  subject,
  body,
  onClose,
  onSent
}: Props) {
  const [copied, setCopied] = useState('');
  const panelRef = useRef<HTMLDivElement>(null);
  const sender = getSenderEmail();
  useEffect(() => {
    panelRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  const copy = (label: string, value: string) => {
    const write = async () => {
      try {
        await navigator.clipboard.writeText(value);
      } catch {
        /* Clipboard blocked — the textarea below is still selectable by hand. */
      }
      setCopied(label);
      window.setTimeout(() => setCopied(''), 1600);
    };
    void write();
  };
  const providers: {
    key: MailClient;
    label: string;
    note: string;
  }[] = [{
    key: 'default',
    label: 'My mail app',
    note: 'Apple Mail, Outlook desktop, Thunderbird'
  }, {
    key: 'gmail',
    label: 'Gmail',
    note: 'Google Workspace or Gmail'
  }, {
    key: 'outlook',
    label: 'Outlook on the web',
    note: 'Microsoft 365 or Outlook.com'
  }];
  const full = `To: ${to}\nFrom: ${sender}\nSubject: ${subject}\n\n${body}`;
  return <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink/70 p-4 py-10" data-id="element-394" data-mp="k1urhi9sv44j">
      <div ref={panelRef} tabIndex={-1} role="dialog" aria-modal="true" aria-labelledby="send-dialog-heading" className="w-full max-w-2xl animate-rise-in border-t-2 border-gold bg-white outline-none" data-id="element-395" data-mp="3qiv19yder59l">
        <div className="flex items-start justify-between gap-4 border-b border-navy-100 px-6 py-5" data-id="element-396" data-mp="3vhen7ys04h1y">
          <div data-id="element-397" data-mp="d2w0iof4z9kh">
            <h2 id="send-dialog-heading" className="font-serif text-[1.25rem] text-ink" data-id="element-398" data-mp="d2duj02o9gzd">
              Open this message in
            </h2>
            <p className="mt-1.5 font-sans text-[0.8125rem] leading-relaxed text-charcoal-500" data-id="element-399" data-mp="86yhgachnp40">
              To {to}, from {sender}. It opens ready to send as an ordinary email.
            </p>
          </div>
          <button type="button" onClick={onClose} className="inline-flex items-center gap-1.5 font-sans text-[0.8125rem] text-charcoal-500 underline underline-offset-2 hover:text-ink" data-id="element-400" data-mp="2ot801s51bckj">
            <XIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-401" data-mp="zd5gsi8qqu3k" />
            Close
          </button>
        </div>

        <ul className="divide-y divide-navy-100" data-id="element-402" data-mp="149vz7c80q1s5">
          {providers.map(p => <li key={p.key} data-id="element-403" data-mp="pl9kncq7te1z">
              <a href={composeUrl(to, subject, body, p.key, sender)} target={p.key === 'default' ? undefined : '_blank'} rel="noopener noreferrer" onClick={onSent} className="group flex items-center justify-between gap-4 px-6 py-4 transition-colors duration-120 hover:bg-parchment" data-id="element-404" data-mp="pm15bsi9xrdh">
                <span data-id="element-405" data-mp="6oqemwapk69n">
                  <span className="block font-sans text-[0.9375rem] text-ink" data-id="element-406" data-mp="6pjfjgmzk1jv">{p.label}</span>
                  <span className="block font-sans text-[0.75rem] text-charcoal-300" data-id="element-407" data-mp="bmryhjfyk9m6">{p.note}</span>
                </span>
                <ExternalLinkIcon aria-hidden="true" className="h-4 w-4 shrink-0 text-charcoal-300 group-hover:text-ink" data-id="element-408" data-mp="20zmz5qz2wh5c" />
              </a>
            </li>)}
        </ul>

        <div className="border-t border-navy-100 bg-parchment px-6 py-5" data-id="element-409" data-mp="yylnyi0q7mw">
          <p className="font-sans text-micro uppercase tracking-[0.14em] text-charcoal-300" data-id="element-410" data-mp="3t2zjkycg0zc4">
            Or copy it and paste into your mailbox
          </p>
          <div className="mt-3 flex flex-wrap gap-2" data-id="element-411" data-mp="3o3ekmnx9wqze">
            <Button size="sm" variant="secondary" onClick={() => copy('to', to)} data-id="element-412" data-mp="3j5d0s0s0wnmv">
              {copied === 'to' ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-413" data-mp="3edl5xe1u3vqx" /> : <CopyIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-414" data-mp="3ec0c5qncggu4" />}
              Recipient
            </Button>
            <Button size="sm" variant="secondary" onClick={() => copy('subject', subject)} data-id="element-415" data-mp="35dex45h2zjy3">
              {copied === 'subject' ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-416" data-mp="30e28bf6ng2sv" /> : <CopyIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-417" data-mp="30dk8y9oox8gq" />}
              Subject
            </Button>
            <Button size="sm" variant="secondary" onClick={() => copy('body', body)} data-id="element-418" data-mp="2vhlatudk8c34">
              {copied === 'body' ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-419" data-mp="1fwazah4kbwun" /> : <CopyIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-420" data-mp="1fuzzese8n98t" />}
              Message
            </Button>
            <Button size="sm" onClick={() => copy('all', full)} data-id="element-421" data-mp="1kw5peigikien">
              {copied === 'all' ? <CheckIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-422" data-mp="1ovo7z7kv4heb" /> : <CopyIcon aria-hidden="true" className="h-3.5 w-3.5" data-id="element-423" data-mp="s6yppg5bpmrt" />}
              Copy everything
            </Button>
          </div>
          <label htmlFor="send-dialog-full" className="mt-4 block font-sans text-[0.75rem] text-charcoal-500" data-id="element-424" data-mp="x6l7m2w3jr6y">
            Full message — select and copy if the buttons are blocked
          </label>
          <textarea id="send-dialog-full" readOnly rows={8} value={full} onFocus={ev => ev.currentTarget.select()} className="mt-2 w-full resize-y border border-navy-200 bg-white px-3 py-2 font-mono text-[0.75rem] leading-relaxed text-charcoal-700" data-id="element-425" data-mp="15ywtrck6gsh4" />
          <div className="mt-4 border-t border-navy-100 pt-4" data-id="element-426" data-mp="1gqdgp5b8v7ye">
            <Button size="sm" variant="secondary" onClick={onSent} data-id="element-427" data-mp="1bsu4sc20r79m">
              Mark as sent
            </Button>
          </div>
        </div>
      </div>
    </div>;
}